import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  Select,
  InputLabel,
  FormControl,
  Alert,
  CircularProgress,
  Tooltip,
  Chip,
  Stack,
  MenuItem,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import Autocomplete from '@mui/material/Autocomplete';
import {
  itemService,
  authService,
  recipeService,
  stockService,
  type Item,
  type Recipe,
  type RecipeComponent,
  type StockItem,
} from '../services';
import { useToast } from '../hooks/useToast';

interface ItemFormData {
  name: string;
  description: string;
  price: string;
  category: string;
  flavors: string;
  newItem: boolean;
  available: boolean;
  recipeId: string;
  recipeComponents: RecipeComponentFormData[];
}

interface RecipeComponentFormData {
  stockItemId: string;
  quantity: string;
  unit: string;
  yieldPercentage?: string;
}

const UNIT_OPTIONS = ['UN', 'KG', 'G', 'L', 'ML', 'CX', 'DZ'];

const DEFAULT_ITEM_FORM: ItemFormData = {
  name: '',
  description: '',
  price: '',
  category: '',
  flavors: '',
  newItem: false,
  available: true,
  recipeId: '',
  recipeComponents: [],
};

const createDefaultFormData = (): ItemFormData => ({
  ...DEFAULT_ITEM_FORM,
  recipeComponents: [],
});

const mapRecipeComponentToForm = (component: RecipeComponent): RecipeComponentFormData => ({
  stockItemId: component.stockItemId,
  quantity: component.quantity ? String(component.quantity) : '',
  unit: component.unit,
  yieldPercentage:
    component.yieldPercentage !== undefined ? String(component.yieldPercentage) : '',
});

const ItemsManagement: React.FC = () => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const [items, setItems] = useState<Item[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingItem, setEditingItem] = useState<Item | null>(null);
  const [formData, setFormData] = useState<ItemFormData>(createDefaultFormData());
  const [dialogLoading, setDialogLoading] = useState(false);
  const [dialogError, setDialogError] = useState('');
  const [cnpjFilter, setCnpjFilter] = useState('');
  const userRole = authService.getUserRole();
  const [, setDetailsItem] = useState<Item | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<Item | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null);
  const [activeCnpj, setActiveCnpj] = useState<string | undefined>(undefined);
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [recipesLoading, setRecipesLoading] = useState(false);
  const [stockItems, setStockItems] = useState<StockItem[]>([]);
  const [stockItemsLoading, setStockItemsLoading] = useState(false);
  const [useRecipe, setUseRecipe] = useState(false);

  const loadItems = useCallback(
    async (filter?: string) => {
      try {
        setLoading(true);
        const trimmedFilter = filter?.trim() || undefined;
        const data = await itemService.listItems(trimmedFilter);
        setItems(data);
        setActiveCnpj(trimmedFilter);
      } catch (err: any) {
        setError(err.message || 'Erro ao carregar itens');
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const handleApplyCnpjFilter = async () => {
    await loadItems(cnpjFilter.trim() || undefined);
  };

  const handleOpenCreateDialog = () => {
    setEditingItem(null);
    setFormData(createDefaultFormData());
    setImageFile(null);
    setPreviewImageUrl(null);
    setDialogError('');
    setUseRecipe(false);
    setOpenDialog(true);
  };

  const handleOpenEditDialog = (item: Item) => {
    setEditingItem(item);
    setFormData({
      name: item.name,
      description: item.description,
      price: String(item.price),
      category: item.category,
      flavors: (item.flavors || []).join(', '),
      newItem: item.newItem,
      available: item.available,
      recipeId: item.recipeId || '',
      recipeComponents: (item.recipeComponents || []).map(mapRecipeComponentToForm),
    });
    setImageFile(null);
    setPreviewImageUrl(item.imageUrl || null);
    setDialogError('');
    setUseRecipe(Boolean(item.recipeId));
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingItem(null);
    setDialogError('');
    setUseRecipe(false);
  };

  const loadStockItemsList = useCallback(
    async (cnpjParam?: string) => {
      try {
        setStockItemsLoading(true);
        const params = cnpjParam ? { cnpj: cnpjParam } : undefined;
        const data = await stockService.listStockItems(params);
        const sorted = [...data].sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));
        setStockItems(sorted);
      } catch (err: any) {
        console.error('Erro ao carregar ingredientes de estoque para ficha técnica', err);
        showError(err.message || 'Erro ao carregar ingredientes de estoque');
      } finally {
        setStockItemsLoading(false);
      }
    },
    [showError],
  );

  const loadRecipesList = useCallback(
    async (cnpjParam?: string) => {
      try {
        setRecipesLoading(true);
        const params = cnpjParam ? { cnpj: cnpjParam } : undefined;
        const data = await recipeService.listRecipes(params);
        const sorted = [...data].sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));
        setRecipes(sorted);
      } catch (err: any) {
        console.error('Erro ao carregar fichas técnicas cadastradas', err);
        showError(err.message || 'Erro ao carregar fichas técnicas');
      } finally {
        setRecipesLoading(false);
      }
    },
    [showError],
  );

  useEffect(() => {
    if (!openDialog) {
      return;
    }
    const cnpjParam = activeCnpj;
    void loadStockItemsList(cnpjParam);
  }, [openDialog, activeCnpj, loadStockItemsList]);

  useEffect(() => {
    if (!openDialog || !useRecipe) {
      return;
    }
    const cnpjParam = activeCnpj;
    void loadRecipesList(cnpjParam);
  }, [openDialog, useRecipe, activeCnpj, loadRecipesList]);

  const resolveStockItem = useCallback(
    (id: string) => stockItems.find((item) => item.id === id) ?? null,
    [stockItems],
  );

  const selectedRecipe = useMemo(
    () => recipes.find((recipe) => recipe.id === formData.recipeId) ?? null,
    [recipes, formData.recipeId],
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setDialogError('');
    setDialogLoading(true);

    const priceNumber = Number(formData.price.replace(',', '.'));
    if (Number.isNaN(priceNumber) || priceNumber < 0) {
      setDialogError('Preço inválido');
      setDialogLoading(false);
      return;
    }

    const flavorsArray = formData.flavors
      .split(',')
      .map((f) => f.trim())
      .filter((f) => f.length > 0);

    const trimmedRecipeId = formData.recipeId.trim();
    const parsedComponents: RecipeComponent[] = [];

    if (useRecipe) {
      if (!trimmedRecipeId) {
        setDialogError('Selecione a ficha técnica que deseja vincular.');
        setDialogLoading(false);
        return;
      }
    } else {
      for (const component of formData.recipeComponents) {
        const stockItemId = component.stockItemId.trim();
        const quantityRaw = component.quantity.trim();
        const unit = component.unit?.trim() || 'UN';
        const yieldRaw = component.yieldPercentage?.trim();

        if (!stockItemId && !quantityRaw && !yieldRaw && !component.unit) {
          continue;
        }

        if (!stockItemId) {
          setDialogError('Selecione o ingrediente em cada linha da ficha técnica.');
          setDialogLoading(false);
          return;
        }

        const quantityNumber = Number(quantityRaw.replace(',', '.'));
        if (!quantityRaw || Number.isNaN(quantityNumber) || quantityNumber <= 0) {
          setDialogError('Informe uma quantidade maior que zero para cada ingrediente.');
          setDialogLoading(false);
          return;
        }

        const normalized: RecipeComponent = {
          stockItemId,
          quantity: quantityNumber,
          unit,
        };

        if (yieldRaw) {
          const yieldNumber = Number(yieldRaw.replace(',', '.'));
          if (Number.isNaN(yieldNumber) || yieldNumber < 0 || yieldNumber > 100) {
            setDialogError('O rendimento deve estar entre 0 e 100.');
            setDialogLoading(false);
            return;
          }
          normalized.yieldPercentage = yieldNumber;
        }

        parsedComponents.push(normalized);
      }
    }

    try {
      let savedItem: Item;
      if (editingItem) {
        savedItem = await itemService.updateItem(editingItem.id, {
          name: formData.name,
          description: formData.description,
          price: priceNumber,
          category: formData.category,
          flavors: flavorsArray,
          newItem: formData.newItem,
          available: formData.available,
          ...(useRecipe
            ? { recipeId: trimmedRecipeId }
            : { recipeId: null, recipeComponents: parsedComponents }),
        });
      } else {
        savedItem = await itemService.createItem({
          name: formData.name,
          description: formData.description,
          price: priceNumber,
          category: formData.category,
          flavors: flavorsArray,
          newItem: formData.newItem,
          available: formData.available,
          ...(useRecipe
            ? { recipeId: trimmedRecipeId }
            : { recipeComponents: parsedComponents }),
        });
      }

      if (imageFile) {
        try {
          await itemService.uploadItemImage(savedItem.id, imageFile);
        } catch (uploadErr: any) {
          console.error('Erro ao enviar imagem do item:', uploadErr);
          showError(uploadErr.message || 'Erro ao enviar imagem do item');
        }
      }

      handleCloseDialog();
      loadItems();
    } catch (err: any) {
      setDialogError(err.message || 'Erro ao salvar item');
    } finally {
      setDialogLoading(false);
    }
  };

  const handleAddRecipeComponent = () => {
    setFormData((prev) => ({
      ...prev,
      recipeComponents: [
        ...prev.recipeComponents,
        { stockItemId: '', quantity: '', unit: 'UN', yieldPercentage: '' },
      ],
    }));
  };

  const handleRemoveRecipeComponent = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      recipeComponents: prev.recipeComponents.filter((_, idx) => idx !== index),
    }));
  };

  const handleRecipeComponentFieldChange = (
    index: number,
    field: keyof RecipeComponentFormData,
    value: string,
  ) => {
    setFormData((prev) => {
      const next = [...prev.recipeComponents];
      next[index] = {
        ...next[index],
        [field]: value,
      };
      return { ...prev, recipeComponents: next };
    });
  };

  const handleRecipeComponentStockItemChange = (index: number, value: StockItem | null) => {
    setFormData((prev) => {
      const next = [...prev.recipeComponents];
      next[index] = {
        ...next[index],
        stockItemId: value?.id ?? '',
        unit: value?.unit || next[index].unit || 'UN',
      };
      return { ...prev, recipeComponents: next };
    });
  };

  const renderRecipeComponentRow = (component: RecipeComponentFormData, index: number) => {
    const selectedStock = resolveStockItem(component.stockItemId);

    return (
      <Paper key={`component-${index}`} variant="outlined" sx={{ p: { xs: 2, sm: 3 } }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="flex-start">
          <Autocomplete
            fullWidth
            options={stockItems}
            loading={stockItemsLoading}
            value={selectedStock}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            getOptionLabel={(option) => `${option.name}${option.sku ? ` • ${option.sku}` : ''}`}
            onChange={(_, value) => handleRecipeComponentStockItemChange(index, value)}
            noOptionsText={stockItemsLoading ? 'Carregando...' : 'Nenhum ingrediente encontrado'}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Ingrediente do estoque"
                required
                helperText={selectedStock ? `Unidade padrão: ${selectedStock.unit}` : undefined}
              />
            )}
          />

          <TextField
            label="Quantidade"
            value={component.quantity}
            onChange={(event) => handleRecipeComponentFieldChange(index, 'quantity', event.target.value)}
            required
            sx={{ width: { xs: '100%', md: 140 } }}
            inputProps={{ inputMode: 'decimal' }}
          />

          <FormControl sx={{ width: { xs: '100%', md: 160 } }}>
            <InputLabel>Unidade</InputLabel>
            <Select
              label="Unidade"
              value={component.unit}
              onChange={(event) => handleRecipeComponentFieldChange(index, 'unit', event.target.value)}
            >
              {UNIT_OPTIONS.map((unit) => (
                <MenuItem key={unit} value={unit}>
                  {unit}
                </MenuItem>
              ))}
              {!UNIT_OPTIONS.includes(component.unit) && component.unit && (
                <MenuItem value={component.unit}>{component.unit}</MenuItem>
              )}
            </Select>
          </FormControl>

          <TextField
            label="Rendimento %"
            value={component.yieldPercentage || ''}
            onChange={(event) => handleRecipeComponentFieldChange(index, 'yieldPercentage', event.target.value)}
            sx={{ width: { xs: '100%', md: 160 } }}
            placeholder="ex: 95"
            inputProps={{ inputMode: 'decimal' }}
            helperText="Opcional"
          />

          <Tooltip title="Remover ingrediente">
            <span>
              <IconButton
                color="error"
                onClick={() => handleRemoveRecipeComponent(index)}
                disabled={dialogLoading}
                sx={{ mt: { xs: 1, md: 0 } }}
              >
                <DeleteIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Paper>
    );
  };

  const handleOpenDeleteDialog = (item: Item) => {
    setItemToDelete(item);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setItemToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!itemToDelete) {
      return;
    }

    try {
      const cnpjParam = cnpjFilter.trim() || undefined;
      await itemService.deleteItem(itemToDelete.id, cnpjParam);
      handleCloseDeleteDialog();
      loadItems(cnpjParam);
      showSuccess(`Item "${itemToDelete.name}" excluído com sucesso.`);
    } catch (err: any) {
      setError(err.message || 'Erro ao excluir item');
      showError(err.message || 'Erro ao excluir item');
    }
  };

  const getCategoryLabel = (category: string) => {
    switch (category) {
      case 'promocao':
        return 'Promoção';
      case 'tradicionais':
        return 'Tradicionais';
      case 'especiais':
        return 'Especiais';
      case 'xis':
        return 'Xis';
      case 'porcoes':
        return 'Porções';
      case 'bebidas':
        return 'Bebidas';
      case 'acompanhamentos':
        return 'Acompanhamentos';
      default:
        return category;
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 2, px: { xs: 1, sm: 2 } }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>

        {userRole === 'DEVELOPER' && (
          <Box
            sx={{
              mb: 2,
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 1,
              alignItems: { xs: 'stretch', sm: 'flex-end' },
            }}
          >
            <TextField
              label="CNPJ (opcional)"
              variant="outlined"
              value={cnpjFilter}
              onChange={(e) => setCnpjFilter(e.target.value)}
              size="small"
              fullWidth
            />
            <Button
              variant="contained"
              onClick={handleApplyCnpjFilter}
              disabled={loading}
              sx={{ minWidth: { sm: 140 } }}
            >
              Aplicar CNPJ
            </Button>
          </Box>
        )}
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 2, px: { xs: 1, sm: 2 } }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Itens do Cardápio
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Gerencie os itens disponíveis para venda no seu cardápio
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Paper elevation={2}>
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Itens</Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenCreateDialog}
            sx={{ mt: { xs: 1, sm: 0 }, alignSelf: { xs: 'stretch', sm: 'auto' } }}
          >
            Novo Item
          </Button>
        </Box>

        <TableContainer sx={{ width: '100%', overflowX: 'auto' }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Nome</TableCell>
                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Categoria</TableCell>
                <TableCell>Preço</TableCell>
                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Novo</TableCell>
                <TableCell>Disponível</TableCell>
                <TableCell>Ficha técnica</TableCell>
                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Sabores</TableCell>
                <TableCell>Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>{item.name}</TableCell>
                  <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{getCategoryLabel(item.category)}</TableCell>
                  <TableCell>
                    {item.price.toLocaleString('pt-BR', {
                      style: 'currency',
                      currency: 'BRL',
                    })}
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                    <Chip
                      label={item.newItem ? 'Sim' : 'Não'}
                      color={item.newItem ? 'secondary' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={item.available ? 'Sim' : 'Não'}
                      color={item.available ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={item.recipeId || (item.recipeComponents?.length ?? 0) > 0 ? 'Vinculada' : 'Sem ficha'}
                      color={item.recipeId || (item.recipeComponents?.length ?? 0) > 0 ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                    {(item.flavors || []).join(', ')}
                  </TableCell>
                  <TableCell>
                    <Tooltip title="Detalhes">
                      <IconButton size="small" onClick={() => setDetailsItem(item)}>
                        <InfoIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Editar">
                      <IconButton size="small" onClick={() => handleOpenEditDialog(item)}>
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Excluir">
                      <IconButton size="small" onClick={() => handleOpenDeleteDialog(item)}>
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingItem ? 'Editar Item' : 'Novo Item'}</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            {dialogError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {dialogError}
              </Alert>
            )}

            <TextField
              autoFocus
              margin="dense"
              label="Nome"
              fullWidth
              variant="outlined"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              disabled={dialogLoading}
            />

            <TextField
              margin="dense"
              label="Descrição"
              fullWidth
              multiline
              minRows={3}
              variant="outlined"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              required
              disabled={dialogLoading}
            />

            <TextField
              margin="dense"
              label="Preço"
              fullWidth
              variant="outlined"
              value={formData.price}
              onChange={(e) => setFormData({ ...formData, price: e.target.value })}
              required
              disabled={dialogLoading}
            />

            <FormControl fullWidth margin="dense" disabled={dialogLoading}>
              <InputLabel>Categoria</InputLabel>
              <Select
                value={formData.category}
                label="Categoria"
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                required
              >
                <MenuItem value="promocao">Promoção</MenuItem>
                <MenuItem value="tradicionais">Tradicionais</MenuItem>
                <MenuItem value="especiais">Especiais</MenuItem>
                <MenuItem value="xis">Xis</MenuItem>
                <MenuItem value="porcoes">Porções</MenuItem>
                <MenuItem value="bebidas">Bebidas</MenuItem>
                <MenuItem value="acompanhamentos">Acompanhamentos</MenuItem>
              </Select>
            </FormControl>

            <TextField
              margin="dense"
              label="Sabores (separados por vírgula)"
              fullWidth
              variant="outlined"
              value={formData.flavors}
              onChange={(e) => setFormData({ ...formData, flavors: e.target.value })}
              disabled={dialogLoading}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={formData.newItem}
                  onChange={(e) => setFormData({ ...formData, newItem: e.target.checked })}
                  disabled={dialogLoading}
                />
              }
              label="Marcar como novo item"
            />

            <FormControlLabel
              control={
                <Switch
                  checked={formData.available}
                  onChange={(e) => setFormData({ ...formData, available: e.target.checked })}
                  disabled={dialogLoading}
                />
              }
              label="Disponível para venda"
            />

            {previewImageUrl && (
              <Box mt={2}>
                <Typography variant="subtitle2" gutterBottom>
                  Imagem atual
                </Typography>
                <Box
                  sx={{
                    borderRadius: 1,
                    overflow: 'hidden',
                    border: '1px solid rgba(0, 0, 0, 0.12)',
                    maxHeight: 240,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 1,
                  }}
                >
                  <img
                    src={previewImageUrl}
                    alt="Imagem do item"
                    style={{ maxWidth: '100%', maxHeight: 240, display: 'block' }}
                  />
                </Box>
              </Box>
            )}

            <Box mt={2}>
              <Button variant="outlined" component="label" disabled={dialogLoading}>
                Selecionar imagem
                <input
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={(event) => {
                    const file = event.target.files?.[0] || null;
                    setImageFile(file);
                  }}
                />
              </Button>
              {imageFile && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Imagem selecionada: {imageFile.name}
                </Typography>
              )}
            </Box>

            <Divider sx={{ my: 3 }} />

            <Box>
              <Typography variant="h6" gutterBottom>
                Ficha técnica do item
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Defina os ingredientes consumidos para produzir uma unidade deste item. Você pode
                vincular uma ficha técnica existente ou cadastrar os ingredientes diretamente aqui.
              </Typography>

              <FormControlLabel
                control={
                  <Switch
                    checked={useRecipe}
                    onChange={(event) => {
                      const checked = event.target.checked;
                      setUseRecipe(checked);
                      setFormData((prev) => ({
                        ...prev,
                        recipeId: checked ? prev.recipeId : '',
                      }));
                    }}
                    disabled={dialogLoading}
                  />
                }
                label="Vincular ficha técnica existente"
              />

              {useRecipe ? (
                <Box sx={{ mt: 2 }}>
                  {recipesLoading ? (
                    <Box display="flex" alignItems="center" gap={2}>
                      <CircularProgress size={24} />
                      <Typography variant="body2" color="text.secondary">
                        Carregando fichas técnicas...
                      </Typography>
                    </Box>
                  ) : recipes.length === 0 ? (
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Nenhuma ficha técnica cadastrada até o momento.
                      </Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => navigate('/recipes')}
                      >
                        Abrir gestão de fichas técnicas
                      </Button>
                    </Paper>
                  ) : (
                    <Autocomplete
                      options={recipes}
                      value={selectedRecipe}
                      getOptionLabel={(option) => option.name}
                      onChange={(_, value) =>
                        setFormData((prev) => ({
                          ...prev,
                          recipeId: value?.id ?? '',
                        }))
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Ficha técnica"
                          required
                          placeholder="Selecione a ficha técnica"
                        />
                      )}
                    />
                  )}
                </Box>
              ) : (
                <Box sx={{ mt: 2 }}>
                  {stockItemsLoading ? (
                    <Box display="flex" alignItems="center" gap={2}>
                      <CircularProgress size={24} />
                      <Typography variant="body2" color="text.secondary">
                        Carregando ingredientes de estoque...
                      </Typography>
                    </Box>
                  ) : (
                    <Stack spacing={2}>
                      {formData.recipeComponents.length === 0 && (
                        <Paper variant="outlined" sx={{ p: 2 }}>
                          <Typography variant="body2" color="text.secondary">
                            Nenhum ingrediente adicionado. Utilize o botão abaixo para incluir os
                            itens de estoque que compõem este produto.
                          </Typography>
                        </Paper>
                      )}

                      {formData.recipeComponents.map((component, index) =>
                        renderRecipeComponentRow(component, index),
                      )}
                    </Stack>
                  )}

                  <Button
                    variant="outlined"
                    sx={{ mt: 2 }}
                    onClick={handleAddRecipeComponent}
                    disabled={dialogLoading || stockItemsLoading || stockItems.length === 0}
                  >
                    Adicionar ingrediente
                  </Button>

                  {stockItems.length === 0 && !stockItemsLoading && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Cadastre itens de estoque para montar a ficha técnica deste produto.
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog} disabled={dialogLoading}>
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={dialogLoading}
              startIcon={dialogLoading ? <CircularProgress size={20} /> : null}
            >
              {dialogLoading ? 'Salvando...' : 'Salvar'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Confirmar exclusão</DialogTitle>
        <DialogContent>
          Tem certeza que deseja excluir o item "{itemToDelete?.name}"?
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Cancelar</Button>
          <Button color="error" variant="contained" onClick={handleConfirmDelete}>
            Excluir
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ItemsManagement;
